// "Nearby" — incidents around the agent's current GPS. Feed-first with a map
// toggle (react-leaflet, severity-colored markers). The empty state is a
// deliberately reassuring moment: no data = good news.
import 'leaflet/dist/leaflet.css';

import { useEffect, useRef, useState } from 'react';
import { CircleMarker, MapContainer, Popup, TileLayer } from 'react-leaflet';

import useGeolocation from '../../../hooks/useGeolocation';
import type { INearbyIncident } from '../../../interface/vesr';
import { getNearbyIncidents } from '../../../lib/vesr-api';
import { RefreshSVG } from '../../utils/svgs';
import TabMessage from '../child-agent/tabs/TabMessage';
import { CalmGlyph, WarnGlyph } from './icons';
import { IncidentCard, type severityOf } from './shared';
import { distanceLabel } from './utils';

type ViewState = 'loading' | 'ready' | 'error' | 'no-location';
type ViewMode = 'feed' | 'map';

const RADII = [1, 5, 10] as const;

interface INearbyProps {
	onCount: (count: number) => void;
}

const Nearby = ({ onCount }: INearbyProps) => {
	const geo = useGeolocation();
	const [state, setState] = useState<ViewState>('loading');
	const [items, setItems] = useState<INearbyIncident[]>([]);
	const [radius, setRadius] = useState<(typeof RADII)[number]>(5);
	const [mode, setMode] = useState<ViewMode>('feed');
	const [refreshing, setRefreshing] = useState(false);
	// Debounce guard — the incidents controller is rate-limited (30/min/IP).
	const lastFetchRef = useRef(0);

	const hasCoords = geo.latitude != null && geo.longitude != null;

	const load = (lat: number, lng: number, r: number, manual = false) => {
		const now = Date.now();
		if (manual && now - lastFetchRef.current < 5_000) return;
		lastFetchRef.current = now;
		if (manual) setRefreshing(true);
		else setState('loading');
		void getNearbyIncidents(lat, lng, r, 50).then((out) => {
			setRefreshing(false);
			if (!out.reachable || out.httpStatus < 200 || out.httpStatus >= 300 || !out.body) {
				setState('error');
				return;
			}
			const data = (out.body.data ?? []).slice().sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));
			setItems(data);
			onCount(data.length);
			setState('ready');
		});
	};

	useEffect(() => {
		if (geo.loading) return;
		if (!hasCoords) {
			setState('no-location');
			return;
		}
		load(geo.latitude!, geo.longitude!, radius);
	}, [geo.loading, hasCoords, radius]);

	if (state === 'no-location') {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='lock'
				title='Location access needed'
				hint={geo.error || 'Allow location access to see incidents reported around you.'}
				action={{ label: 'Retry', onClick: geo.retry }}
			/>
		);
	}

	if (state === 'loading') {
		return (
			<div className='rh-skeleton f-column-15'>
				<div className='rh-skeleton__card skeleton' />
				<div className='rh-skeleton__card skeleton' />
			</div>
		);
	}

	if (state === 'error') {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title='We couldn’t load nearby incidents.'
				hint='The situation room is temporarily unreachable.'
				action={{
					label: 'Try again',
					onClick: () => hasCoords && load(geo.latitude!, geo.longitude!, radius, true),
				}}
			/>
		);
	}

	return (
		<div className='rh-nearby f-column-15'>
			{/* Controls: radius · refresh · feed/map toggle */}
			<div className='rh-nearby__controls'>
				<div
					className='rh-segmented rh-segmented--compact'
					role='radiogroup'
					aria-label='Search radius'
				>
					{RADII.map((r) => (
						<button
							key={r}
							type='button'
							role='radio'
							aria-checked={radius === r}
							className={`rh-segmented__btn ${radius === r ? 'is-active' : ''}`}
							onClick={() => setRadius(r)}
						>
							{r} km
						</button>
					))}
				</div>
				<span className='rh-card__spacer' />
				<button
					type='button'
					className={`rh-nearby__refresh ${refreshing ? 'is-spinning' : ''}`}
					aria-label='Refresh nearby incidents'
					onClick={() => hasCoords && load(geo.latitude!, geo.longitude!, radius, true)}
				>
					<RefreshSVG />
				</button>
				<div
					className='rh-segmented rh-segmented--compact'
					role='radiogroup'
					aria-label='View mode'
				>
					{(['feed', 'map'] as ViewMode[]).map((m) => (
						<button
							key={m}
							type='button'
							role='radio'
							aria-checked={mode === m}
							className={`rh-segmented__btn ${mode === m ? 'is-active' : ''}`}
							onClick={() => setMode(m)}
						>
							{m === 'feed' ? 'Feed' : 'Map'}
						</button>
					))}
				</div>
			</div>

			{!items.length ? (
				<TabMessage
					icon={<CalmGlyph />}
					tone='success'
					title='All calm near you'
					hint={`No active incidents within ${radius} km of your location.`}
				/>
			) : mode === 'map' && hasCoords ? (
				<div className='rh-nearby__map'>
					<MapContainer
						center={[geo.latitude!, geo.longitude!]}
						zoom={radius <= 1 ? 15 : radius <= 5 ? 13 : 12}
						scrollWheelZoom={false}
						style={{ width: '100%', height: '100%' }}
					>
						<TileLayer
							attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
							url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
						/>
						{/* CircleMarker (not Marker) so we don't depend on the default
						    icon-path fix that lives in LocationMap's module. */}
						<CircleMarker
							center={[geo.latitude!, geo.longitude!]}
							radius={7}
							pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.9 }}
						>
							<Popup>You are here</Popup>
						</CircleMarker>
						{items
							.filter((it) => it.incident.location?.latitude != null && it.incident.location?.longitude != null)
							.map((it) => (
								<CircleMarker
									key={it.incident.id}
									center={[it.incident.location.latitude!, it.incident.location.longitude!]}
									radius={9}
									pathOptions={{
										// CSS vars don't reach canvas/SVG paths — resolve the severity
										// tone to a literal for Leaflet.
										color: severityHex(it.incident.severity),
										fillColor: severityHex(it.incident.severity),
										fillOpacity: 0.55,
									}}
								>
									<Popup>
										<strong>{it.incident.title}</strong>
										<br />
										{it.incident.category} · {it.incident.severity} · {distanceLabel(it.distanceKm, it.distanceDisplay)}
									</Popup>
								</CircleMarker>
							))}
					</MapContainer>
				</div>
			) : (
				<div
					className='rh-list f-column-15'
					aria-live='polite'
				>
					{items.map((it) => (
						<IncidentCard
							key={it.incident.id}
							incident={it.incident}
							distance={distanceLabel(it.distanceKm, it.distanceDisplay)}
						/>
					))}
				</div>
			)}
		</div>
	);
};

// Literal hex per severity for Leaflet vector layers (canvas can't read CSS
// vars). Mirrors the --sev-* palette in src/index.css.
const severityHex = (severity: Parameters<typeof severityOf>[0]): string => {
	switch (severity) {
		case 'Critical':
			return '#dc2626';
		case 'High':
			return '#ef4444';
		case 'Medium':
			return '#f59e0b';
		case 'Low':
			return '#3b82f6';
		default:
			return '#6b7280';
	}
};

export default Nearby;
